import { useEffect, useState } from 'react';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import styles from './JoiningPage.module.css';
import typography from '../theme/typography';
import colors from '../theme/colors';
import { isContentfulConfigured } from '../contentful/client';
import { fetchJoiningDaiLabPage } from '../contentful/joiningDaiLabPageApi';

const JoiningPage = () => {
  usePageMeta(pagesSeo.join);
  const [pageData, setPageData] = useState(null);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!isContentfulConfigured()) {
        return;
      }

      setStatus('loading');
      try {
        const data = await fetchJoiningDaiLabPage();
        if (mounted) {
          setPageData(data);
          setStatus('success');
        }
      } catch (err) {
        console.error('Failed to load Joining DAI Lab page from Contentful', err);
        if (mounted) {
          setStatus('error');
        }
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const richTextOptions = {
    renderMark: {
      bold: (text) => (
        <strong style={{ fontFamily: "'Jost', sans-serif", fontSize: '18px', fontStyle: 'normal', fontWeight: 700, lineHeight: '26px', color: colors.dark }}>
          {text}
        </strong>
      ),
    },
    renderNode: {
      paragraph: (node, children) => (
        <p className={styles.body} style={{ fontFamily: "'Jost', sans-serif", fontSize: '18px', fontStyle: 'normal', fontWeight: 400, lineHeight: '26px', color: colors.dark }}>
          {children}
        </p>
      ),
      'heading-1': (node, children) => (
        <h1 className={styles.sectionHeading} style={{ fontFamily: "'Jost', sans-serif", fontSize: '28px', fontStyle: 'normal', fontWeight: 500, lineHeight: '40px', color: colors.dark }}>
          {children}
        </h1>
      ),
      'heading-2': (node, children) => (
        <h2 className={styles.sectionHeading} style={{ fontFamily: "'Jost', sans-serif", fontSize: '28px', fontStyle: 'normal', fontWeight: 500, lineHeight: '40px', color: colors.dark }}>
          {children}
        </h2>
      ),
      'heading-3': (node, children) => (
        <h3 className={styles.subHeading} style={{ fontFamily: "'Jost', sans-serif", fontSize: '28px', fontStyle: 'normal', fontWeight: 500, lineHeight: '40px', color: colors.dark }}>
          {children}
        </h3>
      ),
      'unordered-list': (node, children) => <ul className={styles.list}>{children}</ul>,
      'ordered-list': (node, children) => <ol className={styles.list}>{children}</ol>,
      'list-item': (node, children) => <li>{children}</li>,
      hyperlink: (node, children) => {
        const href = node.data?.uri || '#';
        return (
          <a href={href} className={styles.link}>
            {children}
          </a>
        );
      },
      blockquote: (node, children) => <blockquote>{children}</blockquote>,
      hr: () => <hr className={styles.divider} />,
    },
  };

  const remoteDocument = pageData?.contentDocument;
  const hasRemoteContent =
    remoteDocument?.nodeType === 'document' && Array.isArray(remoteDocument?.content);

  return (
    <div className={styles.page}>
      <Header />

      <section className={styles.section}>
        <div className={styles.container}>
          <h1
            className={`${styles.pageTitle} page-primary-title`}
            style={{ ...typography.heading.large, color: colors.dark }}
          >
            JOINING DAI LAB
          </h1>
          <hr className={styles.divider} />

          <div className={styles.content}>
          {hasRemoteContent ? (
            documentToReactComponents(remoteDocument, richTextOptions)
          ) : (
            <>
              <div className={styles.block}>
                <h2
                  className={styles.sectionHeading}
                  style={{ ...typography.heading.xsmall, color: colors.dark }}
                >
                  Current MIT Students
                </h2>
                <p className={styles.body} style={{ ...typography.body.large.regular, color: colors.dark }}>
                  If you are an MIT student and wish to join DAI Lab for your MEng, UAP, or UROP, send us an email
                  at <a href="mailto:kalyanv@mit.edu" className={styles.link}>kalyanv@mit.edu</a> with:
                </p>
                <ul className={styles.list}>
                  <li>
                    A subject line specifying the opportunity you are seeking and the term for which you are seeking
                    &ldquo;MEng/UAP/UROP Term Year&rdquo;
                  </li>
                  <li>A copy of your transcript</li>
                  <li>An up-to-date resume</li>
                  <li>Your funding needs</li>
                  <li>A list of any open source software contributions you have made</li>
                  <li>
                    You must select one of the following machine learning focus areas:
                    <ul className={styles.subList}>
                      <li>Applications (wind, sustainability, education)</li>
                      <li>Systems for machine learning</li>
                      <li>Cybersecurity</li>
                      <li>Core machine learning</li>
                    </ul>
                  </li>
                </ul>
              </div>

              <div className={styles.block}>
                <h2
                  className={styles.sectionHeading}
                  style={{ ...typography.heading.xsmall, color: colors.dark }}
                >
                  How to get a research assistantship with the group
                </h2>
                <p className={styles.body} style={{ ...typography.body.large.regular, color: colors.dark }}>
                  Research assistantships are available for MEngs who have demonstrated ability to write both software and
                  algorithms. To secure funding here are multiple things you could do (Please note that this does not
                  guarantee funding &mdash; just increases your chances):
                </p>
                <ul className={styles.list}>
                  <li>
                    <strong>Start with teaching assistantship</strong> in fall, work in the lab while teaching, and then
                    apply for lab research assistantship for Spring
                  </li>
                  <li>
                    <strong>Starting as a UAP</strong> in the lab helps us plan ahead for your MEng its funding needs
                  </li>
                  <li>
                    <strong>Contributing to our open source libraries</strong> within{' '}
                    <a href="https://github.com/HDI-Project" className={styles.link}>&ldquo;The human data interaction project&rdquo;</a>
                  </li>
                  <li>
                    <strong>A publication in a peer reviewed conference</strong> during your past UROP/UAP/internship (it
                    helps if you are the primary author)
                  </li>
                </ul>
              </div>

              <div className={styles.block}>
                <h2
                  className={styles.sectionHeading}
                  style={{ ...typography.heading.xsmall, color: colors.dark }}
                >
                  PhD Students
                </h2>
                <p className={styles.body} style={{ ...typography.body.large.regular, color: colors.dark }}>
                  We have multiple openings for PhD students with funding in the areas of machine learning systems,
                  fairness and accountability in machine learning, cybersecurity, artificial intelligence, automated machine
                  learning, and data science automation. To be considered:
                </p>
                <ul className={styles.list}>
                  <li>
                    Applicants must apply through MIT&rsquo;s graduate program admissions process for the Department of
                    Electrical Engineering and Computer Science. (The application deadline is Dec 15, 2018)
                  </li>
                  <li>
                    See{' '}
                    <a
                      href="http://www.eecs.mit.edu/academics-admissions/graduate-program/admissions"
                      className={styles.link}
                    >
                      http://www.eecs.mit.edu/academics-admissions/graduate-program/admissions
                    </a>{' '}
                    for programs of study beginning in Sept 2019
                  </li>
                  <li>
                    Please mention interest in these topics and in doing a PhD under Dr Veeramachaneni in your statement of
                    purpose
                  </li>
                </ul>
                <p className={styles.body} style={{ ...typography.body.large.bold, color: colors.dark }}>
                  For informal inquiries, email{' '}
                  <a href="mailto:kalyan@csail.mit.edu" className={styles.link}>kalyan@csail.mit.edu</a>
                </p>
              </div>

              <div className={styles.block}>
                <h2
                  className={styles.sectionHeading}
                  style={{ ...typography.heading.xsmall, color: colors.dark }}
                >
                  PostDoctoral Researchers
                </h2>
                <p className={styles.body} style={{ ...typography.body.large.regular, color: colors.dark }}>
                  Potential post-docs should contact <strong>Dr. Kalyan Veeramachaneni at{' '}
                    <a href="mailto:kalyan@csail.mit.edu" className={styles.link}>kalyan@csail.mit.edu.</a></strong> It is very
                  important when you contact us to clearly identify areas of interest and provide information about your
                  background.
                </p>

                <h3 className={styles.subHeading} style={{ ...typography.body.large.bold, color: colors.dark }}>
                  Funded postdoctoral positions:
                </h3>
                <p className={styles.body} style={{ ...typography.body.large.regular, color: colors.dark }}>
                  The group has a a few funded post-doctoral associate positions available in the areas of &mdash; Systems for
                  machine learning, Cybersecurity, and Interactive machine learning. If these are your areas of research,
                  then email the following to <a href="mailto:kalyan@csail.mit.edu" className={styles.link}>kalyan@csail.mit.edu</a>:
                </p>
                <ul className={styles.list}>
                  <li>Your CV</li>
                  <li>2 Representative publications</li>
                  <li>A research statement</li>
                  <li>2-3 references</li>
                </ul>

                <h3 className={styles.subHeading} style={{ ...typography.body.large.bold, color: colors.dark }}>
                  Self-funded postdoctoral scholars:
                </h3>
                <p className={styles.body} style={{ ...typography.body.large.regular, color: colors.dark }}>
                  Scholars with funding from other sources (such as research foundations in their own country) are
                  generally welcome. In these situations, if our interests match, a letter of support can be provided for your
                  application process if needed. Post-docs who have a scholarship can apply and
                  email Kalyan at <a href="mailto:kalyan@csail.mit.edu" className={styles.link}>kalyan@csail.mit.edu</a>. If you can send your CV, publications, references, it will help
                  speed up the process.
                </p>
              </div>

              <div className={styles.block}>
                <h2
                  className={styles.sectionHeading}
                  style={{ ...typography.heading.xsmall, color: colors.dark }}
                >
                  Visiting Students
                </h2>
                <p className={styles.body} style={{ ...typography.body.large.regular, color: colors.dark }}>
                  From time to time we welcome students from outside of MIT to pursue their thesis research over a short
                  visit. Note that for these visits, usually the visitor must cover all costs, including MIT visiting student
                  tuition and any other fees associated with visa processing and resources usage. Generally, you must be
                  willing to work on a project of mutual interest and to work on our software libraries and infrastructure. In
                  some cases, we have funding when the project is aligned with our ongoing projects. If you are interested
                  please contact <a href="mailto:kalyan@csail.mit.edu" className={styles.link}>kalyan@csail.mit.edu</a>.
                </p>
              </div>

              <div className={styles.block}>
                <h2
                  className={styles.sectionHeading}
                  style={{ ...typography.heading.xsmall, color: colors.dark }}
                >
                  Visiting Faculty
                </h2>
                <p className={styles.body} style={{ ...typography.body.large.regular, color: colors.dark }}>
                  From time to time we welcome and host faculty from other universities. These visits can be for summer,
                  or last year. Summer is a great time to visit us. These visits usually have to be planned 6 months ahead to
                  allow for space and visit planning. If you are interested to visit please contact{' '}
                  <a href="mailto:kalyan@csail.mit.edu" className={styles.link}>kalyan@csail.mit.edu</a>.
                </p>
              </div>
            </>
          )}

          {status === 'loading' && (
            <p style={{ ...typography.body.small.regular, color: colors.gray[700] }}>Loading latest content…</p>
          )}
          {status === 'error' && (
            <p style={{ ...typography.body.small.regular, color: colors.gray[700] }}>
              Unable to load latest content right now. Showing default information.
            </p>
          )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JoiningPage;
